import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  pointerWithin,
  useDndContext,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {skipToken} from '@reduxjs/toolkit/query';
import {Badge, DataStatus} from 'platform/components';
import {Box, HStack, Image, Show, VStack} from 'platform/foundation';

import {ReactNode} from 'react';

import {descend, isNil, isNotNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery, useListVehicleAlbumsQuery} from '@dms/api/sales';
import {EntityResourceIds, VehicleAlbumResponseBody, VehicleAlbums} from '@dms/api/shared';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useAlbumCopyPlaceholders} from '../../hooks/useAlbumCopyPlaceholders';
import {useBatchDownload} from '../../hooks/useBatchDownload';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {useVehicleAlbumCopyNotification} from '../../hooks/useVehicleAlbumCopyNotification';
import {DownloadDialog} from '../ServiceCasePhotos/components/DownloadDialog';
import {VehicleAlbum} from './VehicleAlbum';
import {VehicleAlbumDragPayload} from './VehicleAlbumDraggablePhoto';
import {VehicleCreateAlbumButton} from './VehicleCreateAlbumButton';

interface VehicleAlbumGalleryProps extends RequiredTestIdProps {
  vehicleId: string;
  serviceCaseId?: string;
  enableCreate?: boolean;
  excludeServiceCaseAlbums?: boolean;
  hideEmptyServiceCaseAlbums?: boolean;
  onAlbumChange?: () => void;
}

// Sale-cycle album names auto-created on every vehicle. Hidden from the service-vehicle /
// service-case gallery since they're only meaningful in the sale-vehicle flow.
const SALE_ALBUM_NAMES = new Set([
  'PHOTOS_CAR_SALES',
  'PHOTOS_CAR_PURCHASE',
  'PHOTOS_CAR_ORIGINAL',
]);

// Below this travel a press stays a click — the lightbox, or the bulk-selection toggle.
const DRAG_ACTIVATION_DISTANCE = 10;
const DROP_TARGET_BORDER = '3px solid';
// Design-system steps, unlike DRAG_ACTIVATION_DISTANCE above, which dnd-kit reads as pixels.
const DRAG_STACK_OFFSET_STEPS = 1;
const DRAG_BADGE_INSET_STEPS = 1;
const SINGLE_PHOTO = 1;
const SINGLE_ALBUM = 1;

// dnd-kit types the payload as an open record, so it has to be narrowed back out on arrival.
const isVehicleAlbumDragPayload = (data: unknown): data is VehicleAlbumDragPayload =>
  isNotNil(data) &&
  typeof data === 'object' &&
  'sourceAlbumId' in data &&
  typeof data.sourceAlbumId === 'string' &&
  'previewUrl' in data &&
  typeof data.previewUrl === 'string' &&
  'moveTo' in data &&
  typeof data.moveTo === 'function';

// The single acceptance test, so the highlight and the drop can never disagree.
const canDropOn = (payload: unknown, albumId: string): payload is VehicleAlbumDragPayload =>
  isVehicleAlbumDragPayload(payload) && payload.sourceAlbumId !== albumId;

// Pick, not DragEndEvent: dnd-kit hands the screen-reader announcement a narrower argument.
const resolveDroppedMove = (event: Pick<DragEndEvent, 'active' | 'over'>) => {
  const payload = event.active.data.current;
  // UniqueIdentifier is string | number, while the move takes an album id.
  const destinationAlbumId = event.over?.id;

  if (typeof destinationAlbumId !== 'string' || !canDropOn(payload, destinationAlbumId)) {
    return null;
  }

  return {moveTo: payload.moveTo, destinationAlbumId};
};

const handleDragEnd = (event: DragEndEvent) => {
  const move = resolveDroppedMove(event);

  if (isNil(move)) {
    return;
  }

  move.moveTo(move.destinationAlbumId);
};

export function VehicleAlbumGallery(props: VehicleAlbumGalleryProps) {
  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const [canCreateVehicleAlbum, canRenameVehicleAlbum, canMoveVehiclePhoto] = usePermissions({
    permissionKeys: ['createVehicleAlbumPhotos', 'renameVehicleAlbum', 'moveVehiclePhoto'],
    scopes: {
      createVehicleAlbumPhotos: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      renameVehicleAlbum: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      moveVehiclePhoto: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const {
    data: albums,
    isLoading,
    isError,
  } = useListVehicleAlbumsQuery({
    vehicleId: props.vehicleId,
  });

  const visibleAlbums =
    albums?.filter((album) => {
      if (SALE_ALBUM_NAMES.has(album.name)) {
        return false;
      }

      if (
        props.excludeServiceCaseAlbums &&
        (isNotNil(album.serviceCaseId) || album.name === VehicleAlbums.PHOTOS_SERVICE_CASE)
      ) {
        return false;
      }

      if (isNotNil(props.serviceCaseId)) {
        return album.serviceCaseId === props.serviceCaseId;
      }

      return true;
    }) ?? [];

  // Move/copy is only offered in the single-service-case view, where every visible album
  // belongs to the same service case. In the aggregated view (no serviceCaseId) the albums
  // span different service cases, so no target list is passed and the feature stays off.
  const isSingleServiceCaseView = isNotNil(props.serviceCaseId);

  // Newest first in every view - sale-cycle albums, the only ones with a meaningful fixed
  // order, never reach this list. Sorted on the instant: raw strings tie-break on the offset.
  const orderedAlbums = visibleAlbums.toSorted(
    descend((album) => parseDate(album.createdAt).getTime())
  );

  const moveCopyTargetAlbums = isSingleServiceCaseView ? orderedAlbums : undefined;

  // Custom Albums only, so a drag cannot cross service cases; the aggregated view can, and stays off.
  const isCustomAlbumView = props.excludeServiceCaseAlbums ?? false;

  // One expression gates the context, the drop zones and the tiles, so a dnd-kit hook added
  // anywhere under this gallery cannot become move-capable by inheriting a live context.
  const isDragEnabled = (isSingleServiceCaseView || isCustomAlbumView) && canMoveVehiclePhoto;

  // A lone album has nothing to drop onto, so no tile offers the gesture.
  const canDragPhotos = isDragEnabled && orderedAlbums.length > SINGLE_ALBUM;

  // Copy is async on the backend: the destination album shows a "copying" placeholder from the
  // moment the copy is fired until the vehicle_photo-copy_finished push clears it (and refreshes
  // the albums). Counted per album so concurrent copies into one album each show a placeholder
  // and are cleared independently — see useAlbumCopyPlaceholders.
  const copyPlaceholders = useAlbumCopyPlaceholders();

  // One batch-download instance for the whole gallery: the progress dialog and the completion
  // push are gallery-wide, so a per-card instance would render a dialog per album. Downloading is
  // only offered for Service Case Albums — the archiving route is scoped to a service album.
  const {handleServiceAlbumDownload, eventData, isProgressVisible, dismissProgress} =
    useBatchDownload();

  const buildAlbumDownloadHandler = (album: VehicleAlbumResponseBody) =>
    album.name === VehicleAlbums.PHOTOS_SERVICE_CASE
      ? () =>
          handleServiceAlbumDownload({
            vehicleId: props.vehicleId,
            // A Vehicle Album's id *is* its Service Album id — the same value the rename route
            // takes as serviceAlbumId.
            serviceAlbumId: album.id,
          })
      : undefined;

  useVehicleAlbumCopyNotification({
    vehicleId: props.vehicleId,
    // Toast + placeholder clearing are only for the copy-capable (single-service-case) view —
    // the same condition that enables the move/copy menu. Cache invalidation is not gated by it.
    enabled: isNotNil(props.serviceCaseId),
    onCopyFinished: copyPlaceholders.markCopyFinished,
  });

  return (
    <VStack spacing={4}>
      <Show when={props.enableCreate && canCreateVehicleAlbum}>
        <HStack justify="flex-end">
          <VehicleCreateAlbumButton
            data-testid={suffixTestId('vehicleAlbumGallery-create', props)}
            vehicleId={props.vehicleId}
            serviceCaseId={props.serviceCaseId}
            onComplete={props.onAlbumChange}
          />
        </HStack>
      </Show>
      <DataStatus isLoading={isLoading} isError={isError} isEmpty={orderedAlbums.length === 0}>
        <VehicleAlbumDndProvider isEnabled={isDragEnabled}>
          <VStack spacing={4}>
            {orderedAlbums.map((album) => {
              const canDelete =
                album.name === VehicleAlbums.PHOTOS_CUSTOM ||
                album.name === VehicleAlbums.PHOTOS_SERVICE_CASE;
              const canRename =
                canRenameVehicleAlbum && album.name === VehicleAlbums.PHOTOS_SERVICE_CASE;
              const testId = suffixTestId(`vehicleAlbumGallery-album-${album.id}`, props);

              if (
                album.name === VehicleAlbums.PHOTOS_SERVICE_CASE &&
                isNotNil(album.serviceCaseId) &&
                isNil(props.serviceCaseId)
              ) {
                return (
                  <ServiceCaseVehicleAlbum
                    key={album.id}
                    data-testid={testId}
                    album={album}
                    vehicleId={props.vehicleId}
                    canDelete={canDelete}
                    canRename={canRename}
                    hideWhenEmpty={props.hideEmptyServiceCaseAlbums}
                    onDownload={buildAlbumDownloadHandler(album)}
                    isDownloadPending={isProgressVisible}
                    onAlbumChange={props.onAlbumChange}
                  />
                );
              }

              return (
                <VehicleAlbumDropZone key={album.id} albumId={album.id} isEnabled={isDragEnabled}>
                  <VehicleAlbum
                    data-testid={testId}
                    vehicleId={props.vehicleId}
                    vehicleAlbumId={album.id}
                    title={album.translation}
                    createdAt={album.createdAt}
                    canUpload
                    canDelete={canDelete}
                    canRename={canRename}
                    hideWhenEmpty={
                      props.hideEmptyServiceCaseAlbums &&
                      album.name === VehicleAlbums.PHOTOS_SERVICE_CASE
                    }
                    moveCopyTargetAlbums={moveCopyTargetAlbums}
                    canBulkSelect={isSingleServiceCaseView}
                    canDragPhotos={canDragPhotos}
                    receivingCopyCount={copyPlaceholders.getPendingCopyCount(album.id)}
                    onCopyStarted={copyPlaceholders.markCopyStarted}
                    onDownload={buildAlbumDownloadHandler(album)}
                    isDownloadPending={isProgressVisible}
                    onAlbumChange={props.onAlbumChange}
                  />
                </VehicleAlbumDropZone>
              );
            })}
          </VStack>
        </VehicleAlbumDndProvider>
      </DataStatus>
      <DownloadDialog
        withWrapper
        isOpen={isProgressVisible}
        onClose={dismissProgress}
        fileName={eventData?.data?.bulkName}
        fileCount={eventData?.data?.objects.length}
      />
    </VStack>
  );
}

interface VehicleAlbumDndProviderProps {
  isEnabled: boolean;
  children: ReactNode;
}

// Mounted only where the gesture is offered: DndContext injects a screen-reader live region.
function VehicleAlbumDndProvider(props: VehicleAlbumDndProviderProps) {
  // MouseSensor, not PointerSensor: the latter also fires for touch, where a scroll swipe would
  // arm the drag and then be cancelled. Touch and keyboard keep right-click and bulk "Move to".
  const sensors = useSensors(
    useSensor(MouseSensor, {activationConstraint: {distance: DRAG_ACTIVATION_DISTANCE}})
  );

  if (!props.isEnabled) {
    return props.children;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      accessibility={{
        screenReaderInstructions: {
          draggable: i18n.t('entity.photo.albumDrag.a11y.instructions'),
        },
        announcements: {
          onDragStart: () => i18n.t('entity.photo.albumDrag.a11y.pickedUp'),
          // Same acceptance test as the highlight: dnd-kit fires this for the source album and
          // for over === null too, where releasing does nothing. Falls back to the pick-up line,
          // which already says what to do next.
          onDragOver: (event) =>
            isNotNil(resolveDroppedMove(event))
              ? i18n.t('entity.photo.albumDrag.a11y.overAlbum')
              : i18n.t('entity.photo.albumDrag.a11y.pickedUp'),
          onDragEnd: (event) =>
            isNotNil(resolveDroppedMove(event))
              ? i18n.t('entity.photo.albumDrag.a11y.moved')
              : i18n.t('entity.photo.albumDrag.a11y.cancelled'),
          onDragCancel: () => i18n.t('entity.photo.albumDrag.a11y.cancelled'),
        },
      }}
      onDragEnd={handleDragEnd}
    >
      {props.children}
      <VehicleAlbumDragPreview />
    </DndContext>
  );
}

function VehicleAlbumDragPreview() {
  const {active} = useDndContext();
  const payload = active?.data.current;

  return (
    // The move is async, so the default animation would fly back onto the still-present tile.
    <DragOverlay dropAnimation={null}>
      <Show when={isVehicleAlbumDragPayload(payload) ? payload : null}>
        {(dragged) => {
          const photoCount = dragged.photoCount ?? SINGLE_PHOTO;
          const isStack = photoCount > SINGLE_PHOTO;

          return (
            <Box position="relative" width="100%" height="100%">
              {/* The Selection travels as one preview, so the stack edge says it is several. */}
              <Show when={isStack}>
                <Box
                  position="absolute"
                  top={DRAG_STACK_OFFSET_STEPS}
                  left={DRAG_STACK_OFFSET_STEPS}
                  width="100%"
                  height="100%"
                  backgroundColor="palettes.neutral.70.100"
                  borderRadius="small"
                />
              </Show>
              <Image
                src={dragged.previewUrl}
                width="100%"
                height="100%"
                fit="cover"
                borderRadius="small"
              />
              <Show when={isStack}>
                <Box
                  position="absolute"
                  top={DRAG_BADGE_INSET_STEPS}
                  right={DRAG_BADGE_INSET_STEPS}
                >
                  <Badge value={photoCount} colorScheme="blue" />
                </Box>
              </Show>
            </Box>
          );
        }}
      </Show>
    </DragOverlay>
  );
}

interface VehicleAlbumDropZoneProps {
  albumId: string;
  isEnabled: boolean;
  children: ReactNode;
}

// Lives here, not in VehicleAlbum: Card takes no ref, so dnd-kit would have nothing to measure.
function VehicleAlbumDropZone(props: VehicleAlbumDropZoneProps) {
  const {setNodeRef, isOver, active} = useDroppable({
    id: props.albumId,
    disabled: !props.isEnabled,
  });
  const payload = active?.data.current;

  // No wrapper when drag is off: an album that renders null would still collect a Stack gap.
  if (!props.isEnabled) {
    return props.children;
  }

  // pointerWithin reports the source album until the pointer leaves it; dropping there is a no-op.
  const isDropTarget = isOver && canDropOn(payload, props.albumId);

  return (
    <Box ref={setNodeRef} position="relative">
      {props.children}
      {/* Drawn over the card instead of around it, so arming a target shifts no layout. */}
      <Show when={isDropTarget}>
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          pointerEvents="none"
          border={DROP_TARGET_BORDER}
          borderColor="palettes.blue.60.100"
          borderRadius="small"
        />
      </Show>
    </Box>
  );
}

interface ServiceCaseVehicleAlbumProps extends RequiredTestIdProps {
  album: VehicleAlbumResponseBody;
  vehicleId: string;
  canDelete: boolean;
  canRename?: boolean;
  hideWhenEmpty?: boolean;
  onDownload?: () => void;
  isDownloadPending?: boolean;
  onAlbumChange?: () => void;
}

function ServiceCaseVehicleAlbum(props: ServiceCaseVehicleAlbumProps) {
  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    isNotNil(props.album.serviceCaseId) ? {serviceCaseId: props.album.serviceCaseId} : skipToken
  );

  const serviceCaseNumber = serviceCase?.number;
  const title = isNotNil(serviceCaseNumber)
    ? `${props.album.translation} · ${serviceCaseNumber}`
    : props.album.translation;

  return (
    <VehicleAlbum
      data-testid={props['data-testid']}
      vehicleId={props.vehicleId}
      vehicleAlbumId={props.album.id}
      title={title}
      createdAt={props.album.createdAt}
      canUpload
      canDelete={props.canDelete}
      canRename={props.canRename}
      hideWhenEmpty={props.hideWhenEmpty}
      onDownload={props.onDownload}
      isDownloadPending={props.isDownloadPending}
      onAlbumChange={props.onAlbumChange}
    />
  );
}
